import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { DynamicUserFields } from './DynamicUserFields';
import { IdentifierFields } from './identifierFields';
import { RoleSelect } from './RoleSelect';
import { useUserCreateFormCore, type UserCreateFormValues } from './useUserCreateFormCore';
import { handleApiError } from '../../api/errorMapping';
import { ApiError } from '../../api/errors';
import { allowedScopesForRole } from '../../lib/roleScopes';
import { Button } from '@/kit';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import type { ScopeDescriptor } from './scopeDescriptors';
import { useDestinationStrategy, type DestinationSubmitResult } from './useDestinationStrategy';

interface Props {
    /** Scope del flujo (TENANT). Decide el comportamiento de destino + creación. */
    descriptor: ScopeDescriptor;
    /**
     * Ref del contexto: el tenantId. Si se omite, se usa `descriptor.useScopeRef()` (el tenantId del actor).
     */
    scopeRef?: string;
    /** `onDone` se llama tras crear OK (ej. cerrar el modal). Si no se pasa, navega a la lista de usuarios. */
    onDone?: () => void;
}

/**
 * Form de alta de usuario del flujo TENANT. Lo común (schema, useForm, query+filtrado de roles, el
 * <select> de rol seccionado) vive acá; el bloque de destino + el submit se delegan a
 * `useDestinationStrategy(descriptor, scopeRef, roleScope)`.
 *
 * Los roles SIEMPRE son los del tenant (`tenantApi.listRoles(tid)`).
 */
export function ScopedUserForm({ descriptor, scopeRef: scopeRefProp, onDone }: Props) {
    const descriptorScopeRef = descriptor.useScopeRef();
    // scopeRef del contexto: prop (drill-down platform) o el tenant del actor (flujo propio).
    const scopeRef = scopeRefProp ?? descriptorScopeRef ?? '';
    const isTenant = descriptor.scope === 'TENANT';

    // DEC-RBAC-079/081: identificador PRIMARIO del tenant del `scopeRef` (propio o del path según el scope).
    const { principalIdentifier } = descriptor.useIdentifier(scopeRef);
    // Etiqueta readonly del destino TENANT (nombre del tenant propio / id del path en platform).
    const tenantDestLabel = descriptor.useTenantDestLabel(scopeRef);

    // El catálogo de roles + customFields y el destino usan el MISMO tenant del scopeRef.
    const core = useUserCreateFormCore({
        catalogTenantId: scopeRef,
        principalIdentifier,
        rolesQueryKey: descriptor.rolesQueryKey(scopeRef),
        listRoles: () => descriptor.listRoles(scopeRef),
        lateralPermission: `${descriptor.permPrefix}.user.create.lateral`,
    });
    const { form, roleGroups, roleId, roleScopeRaw, customFields } = core;

    const queryClient = useQueryClient();
    const navigate = useNavigate();

    // Nivel del destino = scope REAL del rol; fallback a la matriz seed si el rol no trae scope.
    const roleScope = useMemo(() => {
        if (!roleId) return null;
        return roleScopeRaw ?? allowedScopesForRole(roleId)[0];
    }, [roleId, roleScopeRaw]);

    const strategy = useDestinationStrategy({
        scopeRef,
        tid: scopeRef,
        roleScope,
        hasRole: !!roleId,
        createUser: descriptor.api.createUser,
        tenantDestLabel,
    });

    const handleRoleChange = (id: string) => {
        core.setRole(id);
        strategy.resetSelection();
    };

    const mut = useMutation({
        mutationFn: (v: UserCreateFormValues): Promise<DestinationSubmitResult> => {
            if (!roleScope) throw new Error('NO_ROLE_SCOPE');
            return strategy.submit(v, roleScope, principalIdentifier);
        },
        onSuccess: async ({ totalRefs, failedExtra, multiSuccess, multiLabel, failLabel }) => {
            if (failedExtra.length > 0) {
                // El user se creó y al menos el primer destino quedó; reportar los extra que fallaron.
                toast.error(
                    `Usuario creado, pero ${String(failedExtra.length)} de ${String(totalRefs)} ${failLabel} fallaron`,
                );
                handleApiError(failedExtra[0].reason);
            } else if (multiSuccess) {
                toast.success(`Usuario creado con el rol en ${String(totalRefs)} ${multiLabel}`);
            } else {
                toast.success('Usuario creado');
            }
            await queryClient.invalidateQueries({
                queryKey: [...descriptor.queryKeyBase(scopeRef), 'users'],
            });
            if (failedExtra.length === 0) {
                if (onDone) onDone();
                // Sin onDone, solo el flujo con página propia navega a su lista (el drill-down platform
                // vive en un modal → siempre trae onDone y no define `defaultDoneRoute`).
                else if (descriptor.defaultDoneRoute)
                    await navigate({ to: descriptor.defaultDoneRoute });
            }
        },
        onError: (err) => {
            const strat = handleApiError(err);
            if (strat === 'INLINE' && err instanceof ApiError) {
                if (err.code === 'USER_ALREADY_EXISTS') {
                    // El error inline va sobre el campo PRIMARIO del tenant (email o teléfono).
                    if (principalIdentifier === 'PHONE') {
                        form.setError('phone', { message: 'El teléfono ya existe' });
                    } else {
                        form.setError('email', { message: 'Email ya existe' });
                    }
                }
                // SCOPE_INVALID_FOR_ROLE solo aplicaba al flujo TENANT (UserForm). RetailerUserForm no
                // lo mapeaba; se preserva ese matiz.
                if (isTenant && err.code === 'SCOPE_INVALID_FOR_ROLE') {
                    form.setError('roleId', { message: 'Scope inválido para este role' });
                }
            }
        },
    });

    const idPrefix = isTenant ? 'uf' : 'ruf';
    // RetailerUserForm solo mostraba el error de roleId tras submit; UserForm siempre. Se preserva.
    const showRoleError = isTenant || form.formState.isSubmitted;

    return (
        <form
            onSubmit={(e) => {
                void form.handleSubmit((v) => {
                    mut.mutate(v);
                })(e);
            }}
            className="space-y-4"
        >
            {/* DEC-RBAC-079/081: SOLO el identificador primario del tenant (email o teléfono). */}
            <IdentifierFields
                form={form}
                principalIdentifier={principalIdentifier}
                idPrefix={idPrefix}
            />

            <div className="space-y-2">
                <Label htmlFor={`${idPrefix}-name`}>Nombre</Label>
                <Input id={`${idPrefix}-name`} {...form.register('displayName')} />
            </div>

            {/* Campos personalizados del tenant (entre Nombre y Role). No renderiza nada si no hay catálogo. */}
            <DynamicUserFields fields={customFields} form={form} idPrefix={`${idPrefix}-cf`} />

            <RoleSelect
                groups={roleGroups}
                value={roleId}
                onChange={handleRoleChange}
                idPrefix={idPrefix}
                error={showRoleError ? form.formState.errors.roleId?.message : undefined}
            />

            {/* Destino según el scope REAL del rol — delegado a la estrategia del scope. */}
            {strategy.DestinationSelector}

            <Button type="submit" isLoading={mut.isPending} disabled={!strategy.canSubmit}>
                Crear
            </Button>
        </form>
    );
}
